"use client";

import React, { useMemo, useEffect, useCallback, useState, useContext } from "react";
import { SearchFilterInput } from "@thenounproject/lingo-core";

import useAssetLibraryFilters from "@features/library/queries/assetLibraryFilters";
import { RowSize, SearchSortTypes } from "@features/library/types";

import {
  initialState,
  AssetLibraryContext,
  ASSET_ROW_STORAGE_KEY,
  CUSTOM_FIELD_ROW_STORAGE_KEY,
  AssetLibraryContextType,
} from "./AssetLibraryContext";
import { useObject } from "@hooks/useObject";
import { cloneDeep } from "lodash";
import { useSelectSpace } from "@selectors/entities/spaces";
import useAssetLibraryPresets from "@features/library/queries/assetLibraryPresets";

import useAssetViews from "@redux/actions/views/useAssetViews";
import { useSelectedItemsContext } from "@contexts/SelectedItemsProvider";
import { useSearchParams } from "next/navigation";

export default function AssetLibraryProvider({ children }: { children: React.ReactNode }) {
  const { data: filterData } = useAssetLibraryFilters();
  const { clearSelection } = useSelectedItemsContext();
  const space = useSelectSpace();

  // Filter out search_v3-only filters for spaces without that feature
  const availableFilters = useMemo(() => {
    const filters = filterData?.filters || [];
    if (!space?.features?.includes("search_v3")) {
      return filters.filter(f => f.type !== "used-in");
    }
    return filters;
  }, [filterData?.filters, space?.features]);

  const [sort, setSort] = useState<SearchSortTypes>(initialState.sort);
  const [assetRowSize, setAssetRowSize] = useState<RowSize>(initialState.assetRowSize);
  const [showTableCustomFields, setShowTableCustomFields] = useState<boolean>(
    initialState.showTableCustomFields
  );
  const [appliedFilters, setAppliedFilters] = useState<SearchFilterInput[]>(
    initialState.appliedFilters
  );
  const presetViews = useAssetLibraryPresets({ spaceId: space?.id }, { skip: !space?.id });
  const customViews = useAssetViews({ spaceId: space?.id });
  const rawViewId = useSearchParams().get("view");
  const viewIdNum = parseInt(rawViewId);
  const urlViewId: string | number = String(viewIdNum) === rawViewId ? viewIdNum : rawViewId;

  // Picker modals set this to switch custom views without touching the URL, preventing
  // intercepted routes (e.g. @productOverlay) from remounting on navigation.
  // Null defers to the URL-derived viewId. Cleared on picker unmount.
  const [pickerViewId, setPickerViewId] = useState<string | number | null>(null);
  const viewId = pickerViewId ?? urlViewId;

  const activeView = useMemo(
    () => customViews?.data?.find(v => v.id === viewId),
    [viewId, customViews]
  );
  const activePreset = useMemo(
    () => presetViews.data?.find(v => v.id === viewId),
    [presetViews, viewId]
  );

  // Synchronously derive filters from the active view when viewId changes.
  // This ensures children see correct filters on their first render after navigation,
  // preventing a wasted fetch with stale filters.
  const [prevViewId, setPrevViewId] = useState(viewId);
  if (viewId !== prevViewId) {
    setPrevViewId(viewId);
    if (activeView) {
      setAppliedFilters(activeView.filters);
    } else if (activePreset) {
      setAppliedFilters(activePreset.filters);
    }
  }

  useEffect(() => {
    clearSelection({ type: "assetLibrary" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]); // Having the clearSelection dependency here causes an infinite loop

  useEffect(() => {
    localStorage.setItem(ASSET_ROW_STORAGE_KEY, assetRowSize);
  }, [assetRowSize]);

  useEffect(() => {
    localStorage.setItem(CUSTOM_FIELD_ROW_STORAGE_KEY, String(showTableCustomFields));
  }, [showTableCustomFields]);

  const setFilters = useCallback((f: SearchFilterInput[]) => {
    setAppliedFilters(f);
  }, []);

  const addFilter = useCallback(
    (f: SearchFilterInput) => {
      const newFilters = [...appliedFilters, f];
      setAppliedFilters(newFilters);
    },
    [appliedFilters]
  );

  const removeFilter = useCallback(
    (index: number) => {
      const filters = cloneDeep(appliedFilters);
      const newFilters = [...filters.slice(0, index), ...filters.slice(index + 1)];
      setAppliedFilters(newFilters);
    },
    [appliedFilters]
  );

  const clearFilters = useCallback(() => {
    setAppliedFilters([]);
  }, []);

  const setSortBy = useCallback((type: SearchSortTypes) => {
    setSort(type);
  }, []);

  const context: AssetLibraryContextType = useObject({
    availableFilters,
    appliedFilters,
    addFilter,
    removeFilter,
    clearFilters,
    setFilters,
    sort,
    setSortBy,
    assetRowSize,
    setAssetRowSize,
    showTableCustomFields,
    setShowTableCustomFields,
    activeView,
    customViews,
    presetViews,
    activePreset,
    viewId,
    setPickerViewId,
  });

  return <AssetLibraryContext.Provider value={context}>{children}</AssetLibraryContext.Provider>;
}

export const useAssetLibraryContext = () => useContext(AssetLibraryContext);
